import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Tabs,
  Tab,
  Paper,
  AppBar,
  Toolbar,
  Typography,
  ThemeProvider,
  createTheme,
  CssBaseline,
  Button
} from '@mui/material';
import { Settings as SettingsIcon, TableChart as TableIcon, ViewModule as AssortmentIcon, AutoFixHigh as AIIcon, Login as LoginIcon, Build as BuildIcon, ReportProblemOutlined as ExceptionIcon } from '@mui/icons-material';

// Import components
import UIConfigurationEditor from './components/UIConfig/UIConfigurationEditor';
import ViewManager from './components/ViewManager/ViewManager';
import ViewTemplateManager from './components/ViewTemplateManager/ViewTemplateManager';
import MassUpdate from './components/MassUpdate/MassUpdate';
import PipelineManager from './components/PipelineManager/PipelineManager';
import ExceptionAnalysis from './components/ExceptionAnalysis/ExceptionAnalysis';
import UserMenu from './components/Auth/UserMenu';
import CustomerSelector from './components/Customer/CustomerSelector';
import CustomerSelection from './components/Customer/CustomerSelection';
import { CustomerProvider, useCustomer } from './contexts/CustomerContext';
import { TabNavigationProvider } from './contexts/TabNavigationContext';
import { AuthenticationProvider, useAuthContext } from './Auth';
import { ApiProvider } from './config/axios';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 500,
    },
  },
});

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  sx?: Record<string, any>;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, sx, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`app-tabpanel-${index}`}
      aria-labelledby={`app-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3, ...sx }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `app-tab-${index}`,
    'aria-controls': `app-tabpanel-${index}`,
  };
}

function AppContent() {
  // Persist active tab to localStorage so refresh holds context instead of
  // jumping back to Pipeline Manager (tab 0).
  const [tabValue, setTabValue] = useState<number>(() => {
    try {
      const saved = localStorage.getItem('activeTab');
      const parsed = saved !== null ? parseInt(saved, 10) : NaN;
      return Number.isFinite(parsed) && parsed >= 0 && parsed <= 6 ? parsed : 0;
    } catch {
      return 0;
    }
  });
  useEffect(() => {
    try { localStorage.setItem('activeTab', String(tabValue)); } catch { /* storage unavailable */ }
  }, [tabValue]);
  const [customerSelectionOpen, setCustomerSelectionOpen] = useState(false);
  const { token, tokenData, error, logIn, logOut, loginInProgress } = useAuthContext();
  const { currentCustomer, loadLastSelected } = useCustomer();

  // Check if user needs to select customer
  useEffect(() => {
    if (token && !currentCustomer) {
      // Always show customer selection if no customer is set
      setCustomerSelectionOpen(true);
    }
  }, [token, currentCustomer]);

  useEffect(() => {
    if (!!error) {
      console.error("auth error: ", error);
    }
  }, [error]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleLogout = async () => {
    await logOut();
  };

  // Show loading spinner while checking authentication
  if (loginInProgress) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}>
          <Typography variant="h6">Loading...</Typography>
        </Box>
      </ThemeProvider>
    );
  }

  // Show login screen for unauthenticated users
  if (!token) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: 'background.default'
        }}>
          <Paper elevation={3} sx={{ p: 4, maxWidth: 400, width: '100%', mx: 2 }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="h4" component="h1" gutterBottom>
                Unified Configuration Manager
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please sign in to access the application
              </Typography>
            </Box>

            <Button
              fullWidth
              variant="contained"
              size="large"
              startIcon={<LoginIcon />}
              onClick={() => logIn()}
              sx={{ mb: 2 }}
            >
              Sign In
            </Button>
          </Paper>
        </Box>
      </ThemeProvider>
    );
  }

  // Show customer selection if no customer is selected
  if (!currentCustomer) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}>
          <Typography variant="h6">Please select a customer...</Typography>
        </Box>
        <CustomerSelection
          open={customerSelectionOpen}
          onClose={() => setCustomerSelectionOpen(false)}
          mode="first-login"
        />
      </ThemeProvider>
    );
  }

  // Show main application for authenticated users with customer selected
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ flexGrow: 1 }}>
        <AppBar position="static" elevation={0}>
          <Toolbar>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Unified Configuration Manager
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {currentCustomer && <CustomerSelector />}
              <UserMenu user={{ username: tokenData?.email || '', displayName: tokenData?.email || '' }} onLogout={handleLogout} />
            </Box>
          </Toolbar>
        </AppBar>

        <Container maxWidth={false} sx={{ mt: 2 }}>
          <Paper elevation={1} sx={{ width: '100%' }}>
            <TabNavigationProvider activeTab={tabValue} setActiveTab={setTabValue}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                aria-label="configuration manager tabs"
                sx={{ px: 2 }}
              >
                <Tab
                  label="Pipeline Manager"
                  icon={<BuildIcon />}
                  iconPosition="start"
                  {...a11yProps(0)}
                />
                <Tab
                  label="UI Manager"
                  icon={<AssortmentIcon />}
                  iconPosition="start"
                  {...a11yProps(1)}
                />
                <Tab
                  label="View Manager"
                  icon={<SettingsIcon />}
                  iconPosition="start"
                  {...a11yProps(2)}
                />
                <Tab
                  label="View Template Manager"
                  icon={<TableIcon />}
                  iconPosition="start"
                  {...a11yProps(3)}
                />
                <Tab
                  label="Exception Analysis"
                  icon={<ExceptionIcon />}
                  iconPosition="start"
                  {...a11yProps(4)}
                />
                <Tab
                  label="AI Assistant"
                  icon={<AIIcon />}
                  iconPosition="start"
                  {...a11yProps(5)}
                />
              </Tabs>
            </Box>

            <TabPanel value={tabValue} index={0}>
              <PipelineManager />
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <UIConfigurationEditor />
            </TabPanel>

            <TabPanel value={tabValue} index={2} sx={{ p: 0 }}>
              <ViewManager />
            </TabPanel>

            <TabPanel value={tabValue} index={3}>
              <ViewTemplateManager />
            </TabPanel>

            <TabPanel value={tabValue} index={4} sx={{ p: 0 }}>
              <ExceptionAnalysis />
            </TabPanel>

            <TabPanel value={tabValue} index={5}>
              <MassUpdate />
            </TabPanel>
            </TabNavigationProvider>
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
}

function App() {
  return (
    <AuthenticationProvider>
      <ApiProvider>
        <CustomerProvider>
          <AppContent />
        </CustomerProvider >
      </ApiProvider>
    </AuthenticationProvider>
  );
}

export default App;